import React, { useState, useRef, useEffect } from 'react';
import {
    Container,
    SpaceBetween,
    Box,
    Button,
    Input,
    Header,
    Alert,
    Spinner,
    Badge,
} from '@amzn/awsui-components-react';
import CashPositioningAPIClient from './cashPositioningAPIClient';
import { QueryResponse } from './types';

interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    confidence?: number;
    sources?: { sourceType?: string; sourceId?: string; relevance?: number }[];
    timestamp: Date;
}

const SUGGESTED_QUESTIONS = [
    'What wire decisions were made today?',
    'Why was a wire recommended for this entity?',
    'Explain the threshold logic for ZBA sweeps',
    'Show me all accounts with variances',
    'What accounts have AQ positioning wires?',
    'How is the wire amount calculated?',
];

/**
 * AI-powered Q&A panel for explaining wire decisions and querying treasury data.
 * Mirrors the prototype's chat interface using the POST /cash-positioning/query endpoint.
 */
const AIQueryPanel: React.FC = () => {
    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            role: 'assistant',
            content: "Hi! I'm your Cash Positioning AI Assistant. I can answer questions about wire decisions, account balances, and positioning logic. Ask me anything about today's run!",
            timestamp: new Date(),
        },
    ]);
    const [inputValue, setInputValue] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const sendMessage = async (question?: string) => {
        const messageText = question || inputValue.trim();
        if (!messageText || sending) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: messageText,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setSending(true);
        setError(null);

        try {
            const response = await CashPositioningAPIClient.queryCashPositioning(messageText) as QueryResponse;

            const assistantMessage: ChatMessage = {
                role: 'assistant',
                content: response.answer || "I'm processing your request. Let me analyze the data...",
                confidence: response.confidence,
                sources: response.sources,
                timestamp: new Date(),
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (err) {
            const errorMessage: ChatMessage = {
                role: 'assistant',
                content: 'I encountered an issue processing your request. Please try again.',
                timestamp: new Date(),
            };
            setMessages(prev => [...prev, errorMessage]);
            setError(err instanceof Error ? err.message : 'Query failed');
        } finally {
            setSending(false);
        }
    };

    const getConfidenceBadge = (confidence?: number) => {
        if (confidence == null) return null;
        if (confidence >= 0.8) return <Badge color="green">High confidence</Badge>;
        if (confidence >= 0.5) return <Badge color="blue">Medium confidence</Badge>;
        return <Badge color="grey">Low confidence</Badge>;
    };

    return (
        <Container>
            <SpaceBetween size="m">
                <Header
                    variant="h2"
                    description="Ask natural language questions about wire decisions, account balances, and positioning logic"
                >
                    AI Assistant — Cash Positioning Q&A
                </Header>

                {/* Suggested questions */}
                <Box>
                    <Box variant="small" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                        Quick questions:
                    </Box>
                    <SpaceBetween direction="horizontal" size="xs">
                        {SUGGESTED_QUESTIONS.slice(0, 3).map((q, idx) => (
                            <Button
                                key={idx}
                                variant="link"
                                onClick={() => sendMessage(q)}
                                disabled={sending}
                            >
                                {q}
                            </Button>
                        ))}
                    </SpaceBetween>
                    <SpaceBetween direction="horizontal" size="xs">
                        {SUGGESTED_QUESTIONS.slice(3).map((q, idx) => (
                            <Button
                                key={idx}
                                variant="link"
                                onClick={() => sendMessage(q)}
                                disabled={sending}
                            >
                                {q}
                            </Button>
                        ))}
                    </SpaceBetween>
                </Box>

                {/* Chat messages */}
                <div
                    style={{
                        maxHeight: '500px',
                        overflowY: 'auto',
                        border: '1px solid #e9ebed',
                        borderRadius: '8px',
                        padding: '16px',
                        background: '#fafafa',
                    }}
                >
                    <SpaceBetween size="m">
                        {messages.map((msg, idx) => (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                }}
                            >
                                <div
                                    style={{
                                        maxWidth: '80%',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        background: msg.role === 'user' ? '#0972d3' : '#ffffff',
                                        color: msg.role === 'user' ? '#ffffff' : '#000716',
                                        border: msg.role === 'assistant' ? '1px solid #e9ebed' : 'none',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                    }}
                                >
                                    <Box variant="p" margin={{ bottom: 'xxs' }}>
                                        {msg.content}
                                    </Box>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                        <Box variant="small" color={msg.role === 'user' ? 'inherit' : 'text-body-secondary'}>
                                            {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </Box>
                                        {msg.role === 'assistant' && getConfidenceBadge(msg.confidence)}
                                    </div>
                                    {msg.sources && msg.sources.length > 0 && (
                                        <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                                            Sources: {msg.sources.map(s => s.sourceType).join(', ')}
                                        </Box>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sending && (
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <div
                                    style={{
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        background: '#ffffff',
                                        border: '1px solid #e9ebed',
                                    }}
                                >
                                    <Spinner size="normal" /> Thinking...
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </SpaceBetween>
                </div>

                {/* Input */}
                <SpaceBetween direction="horizontal" size="xs">
                    <div style={{ flex: 1 }}>
                        <Input
                            value={inputValue}
                            onChange={({ detail }) => setInputValue(detail.value)}
                            onKeyDown={({ detail }) => {
                                if (detail.key === 'Enter') {
                                    sendMessage();
                                }
                            }}
                            placeholder="Ask about wire decisions, account balances, positioning logic..."
                            disabled={sending}
                        />
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => sendMessage()}
                        disabled={!inputValue.trim() || sending}
                        loading={sending}
                    >
                        Send
                    </Button>
                </SpaceBetween>

                {error && (
                    <Alert type="warning" dismissible onDismiss={() => setError(null)}>
                        {error}
                    </Alert>
                )}
            </SpaceBetween>
        </Container>
    );
};

export default AIQueryPanel;
